import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Paths } from '../../../app/constants/paths';
import { useTenant } from '../../../app/context/tenant-context';
import {
  useAnomalousTags,
  useDeployedModels,
  useSubSystemsById,
  useTagsByName,
} from '../../../app/hooks/folders';
import { useViewContext } from '../../context/view-context';
import { TARGET_TYPE } from '../../pages/alertManager/alert-manager';
import { AlertModalV2 } from '../alert-modal/alert-modal-v2';

import { Dropdown } from '@controlrooms/components';
import {
  AnomalySort,
  AssessMode,
  DeployedModelType,
  HiddenTag,
  Model,
  ModelType,
  ParentTag,
  Tag,
  ViewType,
} from '@controlrooms/models';

interface MenuPosition {
  x: number;
  y: number;
}

interface PortalMenuContextType {
  openMenu: (
    type: 'tag' | 'system' | 'model',
    data: Tag | number | Model,
    position: MenuPosition,
  ) => void;
  closeMenu: () => void;
  isOpen: boolean;
  menuType: 'tag' | 'system' | 'model' | null;
  menuData: Tag | number | Model | null;
  menuPosition: MenuPosition | null;
  openAlertModal: (type: 'tag' | 'system' | 'model', data: Tag | number | Model) => void;
  closeAlertModal: () => void;
  isAlertModalOpen: boolean;
}

const PortalMenuContext = createContext<PortalMenuContextType | null>(null);

export const usePortalMenu = () => {
  const context = useContext(PortalMenuContext);
  if (!context) {
    throw new Error('usePortalMenu must be used within PortalMenuProvider');
  }
  return context;
};

const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const MenuContainer = styled.div<{ x?: number; y?: number }>`
  position: fixed;
  left: ${(props) => props.x || 0}px;
  top: ${(props) => props.y || 0}px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  min-width: 150px;
`;

export const PortalMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuType, setMenuType] = useState<'tag' | 'system' | 'model' | null>(null);
  const [menuData, setMenuData] = useState<Tag | number | Model | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertModalType, setAlertModalType] = useState<'tag' | 'system' | 'model' | null>(null);
  const [alertModalData, setAlertModalData] = useState<Tag | number | Model | null>(null);

  const openMenu = useCallback(
    (type: 'tag' | 'system' | 'model', data: Tag | number | Model, position: MenuPosition) => {
      setMenuType(type);
      setMenuData(data);
      setMenuPosition(position);
      setIsOpen(true);
    },
    [],
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setMenuType(null);
    setMenuData(null);
    setMenuPosition(null);
  }, []);

  const openAlertModal = useCallback(
    (type: 'tag' | 'system' | 'model', data: Tag | number | Model) => {
      setAlertModalType(type);
      setAlertModalData(data);
      setIsAlertModalOpen(true);
    },
    [],
  );

  const closeAlertModal = useCallback(() => {
    setIsAlertModalOpen(false);
    setAlertModalType(null);
    setAlertModalData(null);
  }, []);

  const renderMenuContent = () => {
    if (!isOpen || !menuType || !menuData || !menuPosition) return null;
    if (menuType === 'tag') {
      return (
        <TagMenuContent tag={menuData as Tag} onClose={closeMenu} openAlertModal={openAlertModal} />
      );
    } else if (menuType === 'system') {
      return (
        <SystemMenuContent
          system={menuData as number}
          onClose={closeMenu}
          openAlertModal={openAlertModal}
        />
      );
    } else if (menuType === 'model') {
      return (
        <ModelMenuContent
          model={menuData as Model}
          onClose={closeMenu}
          openAlertModal={openAlertModal}
        />
      );
    }
    return null;
  };

  // Only render overlay if dropdown is open and modal is not open
  const showOverlay = isOpen && !isAlertModalOpen;

  return (
    <PortalMenuContext.Provider
      value={{
        openMenu,
        closeMenu,
        isOpen,
        menuType,
        menuData,
        menuPosition,
        openAlertModal,
        closeAlertModal,
        isAlertModalOpen,
      }}
    >
      {children}
      {isOpen &&
        createPortal(
          <>
            {showOverlay && <MenuOverlay onClick={closeMenu} />}
            <MenuContainer x={menuPosition?.x} y={menuPosition?.y}>
              {renderMenuContent()}
            </MenuContainer>
          </>,
          document.body,
        )}
      {isAlertModalOpen && alertModalType && alertModalData && (
        <AlertModalPortal type={alertModalType} data={alertModalData} onClose={closeAlertModal} />
      )}
    </PortalMenuContext.Provider>
  );
};

// Helper to render AlertModalV2 outside dropdown
interface AlertModalPortalProps {
  type: 'tag' | 'system' | 'model';
  data: Tag | number | Model;
  onClose: () => void;
}
const AlertModalPortal: React.FC<AlertModalPortalProps> = ({ type, data, onClose }) => {
  const { data: tagsByName } = useTagsByName();
  if (type === 'tag') {
    const tag = data as Tag;
    const tagObj = tagsByName[tag.name];
    if (tagObj) {
      return (
        <AlertModalV2
          onConfirm={onClose}
          onCancel={onClose}
          alert={{
            alertType: TARGET_TYPE.TAG,
            systemId: tagObj.folder as number,
            tags: [tagObj],
          }}
        />
      );
    }
    return null;
  } else if (type === 'system') {
    const system = data as number;
    return (
      <AlertModalV2
        onConfirm={onClose}
        onCancel={onClose}
        alert={{
          alertType: TARGET_TYPE.SYSTEM,
          systemId: system,
          tags: [],
        }}
      />
    );
  }
  return null;
};

// Tag Menu Content Component
interface TagMenuContentProps {
  tag: Tag;
  onClose: () => void;
  openAlertModal: (type: 'tag' | 'system' | 'model', data: Tag | number | Model) => void;
}
const TagMenuContent: React.FC<TagMenuContentProps> = ({ tag, onClose, openAlertModal }) => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { viewState, setViewState } = useViewContext();
  const assessMode = viewState.config[ViewType.ANALYZE].assessMode;
  // const { data: tagsByName } = useTagsByName(); // not needed here

  const pinnedTags = useMemo(() => viewState.view[ViewType.ANALYZE]?.pinnedTags || [], [viewState]);
  const investigateSelectedTags = useMemo(
    () => viewState.view[ViewType.INVESTIGATE]?.selectedTags || {},
    [viewState],
  );

  const isCurrentTagPinned = useMemo(() => {
    return pinnedTags?.some((_tag) => _tag.name === tag?.name);
  }, [pinnedTags, tag?.name]);
  const isCurrentTagInvestigated = useMemo(() => {
    return investigateSelectedTags[tag.folder || -1]?.some((_tag) => _tag.name === tag.name);
  }, [investigateSelectedTags, tag.folder, tag.name]);

  const handleInvestigate = () => {
    if (isCurrentTagInvestigated) {
      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.INVESTIGATE]: {
            ...prev.view[ViewType.INVESTIGATE],
            selectedTags: {
              ...prev.view[ViewType.INVESTIGATE].selectedTags,
              [tag.folder || -1]: prev.view[ViewType.INVESTIGATE].selectedTags[
                tag.folder || -1
              ].filter((_tag) => _tag.name !== tag.name),
            },
          },
        },
      }));
      onClose();
      return;
    }
    setViewState((prev) => ({
      ...prev,
      view: {
        ...prev.view,
        [ViewType.INVESTIGATE]: {
          ...prev.view[ViewType.INVESTIGATE],
          selectedTags: {
            ...prev.view[ViewType.INVESTIGATE].selectedTags,
            [tag.folder || -1]: [
              ...(prev.view[ViewType.INVESTIGATE].selectedTags[tag.folder || -1] || []),
              tag as Tag,
            ],
          },
        },
      },
    }));
    onClose();
  };

  const handlePin = () => {
    if (isCurrentTagPinned) {
      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.ANALYZE]: {
            ...prev.view[ViewType.ANALYZE],
            pinnedTags: (prev.view[ViewType.ANALYZE].pinnedTags || []).filter(
              (_tag) => !(_tag.name === tag.name && _tag.folder === tag.folder),
            ),
          },
        },
      }));
      onClose();
      return;
    }
    setViewState((prev) => ({
      ...prev,
      view: {
        ...prev.view,
        [ViewType.ANALYZE]: {
          ...prev.view[ViewType.ANALYZE],
          pinnedTags: [...(prev.view[ViewType.ANALYZE].pinnedTags || []), tag as ParentTag],
        },
      },
    }));
    onClose();
  };

  const handleModel = () => {
    navigate(
      `${generatePath(Paths.MANAGE_V2_MODELS, { tenantId: tenant.toString() })}?tag=${
        tag.name
      }&folder=${tag.folder}`,
    );
    onClose();
  };

  const handleHide = () => {
    const hiddenTag: HiddenTag = {
      folder: tag.folder || -1,
      tag: tag.name,
    };
    setViewState((prev) => ({
      ...prev,
      view: {
        ...prev.view,
        [ViewType.ANALYZE]: {
          ...prev.view[ViewType.ANALYZE],
          hiddenTags: Array.from(
            new Set([...(prev.view[ViewType.ANALYZE].hiddenTags || []), hiddenTag]),
          ),
        },
      },
    }));
    onClose();
  };

  return (
    <Dropdown.Options position="right" open={true}>
      <Dropdown.Title>Tag Actions</Dropdown.Title>
      <Dropdown.Item onClick={handleInvestigate}>
        {isCurrentTagInvestigated ? 'Remove from Investigate' : 'Investigate'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handlePin}>{isCurrentTagPinned ? 'Unpin' : 'Pin'}</Dropdown.Item>
      {!isCurrentTagPinned && assessMode !== AssessMode.MODEL && (
        <Dropdown.Item onClick={handleHide}>Hide</Dropdown.Item>
      )}
      <Dropdown.Title>Manage</Dropdown.Title>
      <Dropdown.Item onClick={handleModel}>Model</Dropdown.Item>
      <Dropdown.Item
        onClick={() => {
          openAlertModal('tag', tag);
          onClose();
        }}
      >
        Alerts
      </Dropdown.Item>
    </Dropdown.Options>
  );
};

interface SystemMenuContentProps {
  system: number;
  onClose: () => void;
  openAlertModal: (type: 'tag' | 'system' | 'model', data: Tag | number | Model) => void;
}

interface ModelMenuContentProps {
  model: Model;
  onClose: () => void;
  openAlertModal: (type: 'tag' | 'system' | 'model', data: Tag | number | Model) => void;
}
const SystemMenuContent: React.FC<SystemMenuContentProps> = ({
  system,
  onClose,
  openAlertModal,
}) => {
  const { viewState, setViewState } = useViewContext();
  const { data: systemData } = useSubSystemsById();
  const assessMode = viewState.config[ViewType.ANALYZE].assessMode;
  const { getTag } = useTagsByName();

  const timeSelection = viewState.timeSelection;
  const anomalousSystems = viewState.view[ViewType.ANALYZE].selectedFolders;
  const payload = {
    startTime: timeSelection.startTime,
    endTime: timeSelection.endTime,
    searchKeys: [],
    streamingTimeInSeconds: timeSelection.streamingTimeInSeconds,
  };

  const { data: anomalousAnalyzeTags } = useAnomalousTags(
    payload,
    anomalousSystems || [],
    viewState.config?.[ViewType.ANALYZE]?.sortOption || AnomalySort.SEVERITY,
    true,
  );

  // Get deployed models for MODEL mode
  const ensembleFamilyId = viewState.selectedEnsemble?.family_id || '';
  const selectedTags = useMemo(() => {
    const tags: { tag_name: string; process_id: number }[] = [];
    const pinnedTags = viewState.view[ViewType.ANALYZE].pinnedTags || [];
    if (pinnedTags) {
      pinnedTags.forEach((pinnedTag) => {
        tags.push({
          tag_name: pinnedTag.name,
          process_id: pinnedTag.folder,
        });
      });
    }
    return tags;
  }, [viewState.view]);

  const selectedEnsembleType = useMemo(() => {
    const modelFilterTypes = viewState.config?.[ViewType.ANALYZE]?.modelFilterTypes;
    if (!modelFilterTypes || modelFilterTypes.length === 0) return undefined;
    if (modelFilterTypes.length === 2) return undefined;
    return modelFilterTypes[0] as 'UNIVARIATE' | 'MULTIVARIATE' | undefined;
  }, [viewState.config]);

  const { data: deployedModels = [] } = useDeployedModels(
    anomalousSystems || [],
    selectedTags,
    ensembleFamilyId,
    selectedEnsembleType,
    Boolean(false),
    assessMode === AssessMode.MODEL && (anomalousSystems.length > 0 || selectedTags.length > 0),
  );

  const isCurrentSystemInvestigated = useMemo(() => {
    if (!viewState.view[ViewType.INVESTIGATE].selectedTags[system] || !systemData[system]?.tags) {
      return false;
    }
    return viewState.view[ViewType.INVESTIGATE].selectedTags[system]?.length;
  }, [viewState, system, systemData]);

  const isCurrentSystemPinned = useMemo(() => {
    return !!viewState.view[ViewType.ANALYZE].pinnedTags?.some(
      (pinnedTag) => pinnedTag.folder === system,
    );
  }, [system, viewState.view]);

  // Common function to get system tags based on AssessMode
  const getSystemTags = useCallback((): Tag[] => {
    if (assessMode === AssessMode.MODEL) {
      if (deployedModels.length === 0) {
        return (
          (systemData[system]?.tags || [])
            .map((tag) => getTag(tag.name, system)[0])
            .filter((tag): tag is Tag => tag !== undefined) || []
        );
      }
      // In MODEL mode, get visible tags from deployed models
      const systemModels = deployedModels.filter((model) => model.process_id === system);
      const visibleTags: Tag[] = [];

      systemModels.forEach((model) => {
        if (model.type === (DeployedModelType.DECONSTRUCTED_MODEL as unknown as ModelType)) {
          // For DECONSTRUCTED_MODEL, use anomalous tags in order but only if they exist in independent tags
          const independentTags = model.independent_tags || [];
          const anomalousTagsInOrder =
            anomalousAnalyzeTags?.find((tag) => tag.folder === system)?.tags || [];

          // Filter anomalous tags to only include those that exist in independent tags
          const filteredAnomalousTags = anomalousTagsInOrder.filter((tagName) =>
            independentTags.includes(tagName),
          );

          filteredAnomalousTags.forEach((tagName) => {
            const tagObj = getTag(tagName, system)[0];
            if (tagObj) {
              visibleTags.push(tagObj);
            }
          });
        } else {
          // For UNIVARIATE models, pin the dependent tags
          const dependentTags = model.dependent_tags || [];
          dependentTags.forEach((tag) => {
            const tagObj = getTag(tag.name, system)[0];
            if (tagObj) {
              visibleTags.push(tagObj);
            }
          });

          // Get OpMode tag (always visible)
          if (model.op_mode) {
            const opModeTag = getTag(model.op_mode)[0];
            if (opModeTag) {
              visibleTags.push(opModeTag);
            }
          }

          // Get independent tags (always visible in MODEL mode)
          const independentTags = model.independent_tags || [];
          independentTags.forEach((tagName) => {
            const tagObj = getTag(tagName)[0];
            if (tagObj) {
              visibleTags.push(tagObj);
            }
          });
        }
      });

      // Remove duplicates
      return visibleTags.filter(
        (tag, index, self) => index === self.findIndex((t) => t.name === tag.name),
      );
    } else {
      // In SYSTEM mode, use the current logic
      if (viewState.view[ViewType.ANALYZE].selectedFolders.includes(system)) {
        const hiddenTags = (viewState.view[ViewType.ANALYZE].hiddenTags || []).map(
          (tag) => tag.tag,
        );
        let tagsToInvestigate = (
          anomalousAnalyzeTags?.find((tf) => {
            return tf.folder === system;
          })?.tags || []
        ).filter((tag) => !hiddenTags.includes(tag));

        const pinnedTags = (viewState.view[ViewType.ANALYZE]?.pinnedTags || []).map(
          (tag) => tag.name,
        );
        if (pinnedTags.length) {
          tagsToInvestigate = Array.from(new Set([...tagsToInvestigate, ...pinnedTags]));
        }
        return tagsToInvestigate
          .map((tagName) => getTag(tagName, system)[0])
          .filter((tag): tag is Tag => tag !== undefined);
      } else {
        return (
          (systemData[system]?.tags || [])
            .map((tag) => getTag(tag.name, system)[0])
            .filter((tag): tag is Tag => tag !== undefined) || []
        );
      }
    }
  }, [
    assessMode,
    deployedModels,
    system,
    anomalousAnalyzeTags,
    getTag,
    viewState.view,
    systemData,
  ]);

  const handleInvestigate = () => {
    if (!viewState.view[ViewType.INVESTIGATE].selectedTags[system] && !systemData[system]?.tags) {
      return;
    }
    if ((viewState.view[ViewType.INVESTIGATE].selectedTags[system] || []).length) {
      const newSelectedTags = { ...viewState.view[ViewType.INVESTIGATE].selectedTags };
      delete newSelectedTags[system];
      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.INVESTIGATE]: {
            ...prev.view[ViewType.INVESTIGATE],
            selectedTags: newSelectedTags,
          },
        },
      }));
      onClose();
      return;
    }

    const systemTagsToInvestigate = getSystemTags();

    const tagsGroupedByFolder: Record<number, Tag[]> = {};

    systemTagsToInvestigate.forEach((tag) => {
      const tagFolder = tag.folder || -1;

      if (!tagsGroupedByFolder[tagFolder]) {
        tagsGroupedByFolder[tagFolder] = [];
      }
      tagsGroupedByFolder[tagFolder].push(tag);
    });

    setViewState((prev) => {
      return {
        ...prev,
        view: {
          ...prev.view,
          [ViewType.INVESTIGATE]: {
            ...prev.view[ViewType.INVESTIGATE],
            selectedTags: {
              ...prev.view[ViewType.INVESTIGATE].selectedTags,
              ...tagsGroupedByFolder,
            },
          },
        },
      };
    });
    onClose();
  };

  const handlePinAllTags = () => {
    if (isCurrentSystemPinned) {
      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.ANALYZE]: {
            ...prev.view[ViewType.ANALYZE],
            pinnedTags: (prev.view[ViewType.ANALYZE].pinnedTags || []).filter((pinnedTag) => {
              const tag = systemData[system]?.tags.find((_tag) => _tag.name === pinnedTag.name);
              return tag ? tag.folder === system : true;
            }),
            hiddenTags: Array.from(
              new Set([
                ...(prev.view[ViewType.ANALYZE].hiddenTags || []).filter(
                  (hiddenTag) => hiddenTag.folder !== system,
                ),
              ]),
            ),
          },
        },
      }));
    } else {
      const systemTagsToPin = getSystemTags();

      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.ANALYZE]: {
            ...prev.view[ViewType.ANALYZE],
            pinnedTags: [
              ...(prev.view[ViewType.ANALYZE].pinnedTags || []),
              ...(systemTagsToPin?.map((tag) => ({ ...tag, folder: system })) || []),
            ],
          },
        },
      }));
    }
    onClose();
  };

  const handleHideTags = () => {
    const hiddenTags = (viewState.view[ViewType.ANALYZE].hiddenTags || []).filter(
      (tag) => tag.folder === system,
    );
    //this should only hide unpinned tags that are anomalousAnalyzeTags and hiddenTags shoud be of type HiddenTag
    const unpinnedTags: HiddenTag[] =
      anomalousAnalyzeTags
        ?.find((tf) => tf.folder === system)
        ?.tags.filter(
          (tag) =>
            tag && // Filter out empty/null tags
            !viewState.view[ViewType.ANALYZE].pinnedTags?.some(
              (pinnedTag) => pinnedTag.name === tag,
            ),
        )
        .map((tag) => ({
          folder: system,
          tag: tag,
        })) || [];
    const unHiddenUnPinnedTags = unpinnedTags.filter(
      (tag) => !hiddenTags.some((hiddenTag) => hiddenTag.tag === tag.tag),
    );
    setViewState((prev) => ({
      ...prev,
      view: {
        ...prev.view,
        [ViewType.ANALYZE]: {
          ...prev.view[ViewType.ANALYZE],
          hiddenTags: Array.from(
            new Set([...(prev.view[ViewType.ANALYZE].hiddenTags || []), ...unHiddenUnPinnedTags]),
          ),
        },
      },
    }));
  };

  //TODO: Enable when we implement multivariate models
  // const handleModel = () => {
  //   navigate(
  //     `${generatePath(Paths.MANAGE_V2_MODELS, {
  //       tenantId: tenant.toString(),
  //     })}?folder=${system}`,
  //   );
  //   onClose();
  // };

  return (
    <Dropdown.Options position="right" open={true}>
      <Dropdown.Title>System Actions</Dropdown.Title>
      <Dropdown.Item onClick={handleInvestigate}>
        {isCurrentSystemInvestigated ? 'Remove from Investigate' : 'Investigate all tags'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handlePinAllTags}>
        {isCurrentSystemPinned ? 'Unpin all tags' : 'Pin all tags'}
      </Dropdown.Item>
      {assessMode !== AssessMode.MODEL && (
        <Dropdown.Item onClick={handleHideTags}>Hide all tags</Dropdown.Item>
      )}
      <Dropdown.Title>Manage</Dropdown.Title>
      <Dropdown.Item
        onClick={() => {
          openAlertModal('system', system);
          onClose();
        }}
      >
        Alerts
      </Dropdown.Item>
    </Dropdown.Options>
  );
};

const ModelMenuContent: React.FC<ModelMenuContentProps> = ({ model, onClose }) => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { viewState, setViewState } = useViewContext();
  const { getTag } = useTagsByName();

  const timeSelection = viewState.timeSelection;
  const anomalousSystems = viewState.view[ViewType.ANALYZE].selectedFolders;
  const payload = {
    startTime: timeSelection.startTime,
    endTime: timeSelection.endTime,
    searchKeys: [],
    streamingTimeInSeconds: timeSelection.streamingTimeInSeconds,
  };

  // Compute showAnalyzeLimits from limitFilters
  const limitFilters = viewState.config?.[ViewType.ANALYZE]?.limitFilters;
  const showAnalyzeLimits = useMemo(() => {
    if (!limitFilters) return undefined;

    const values = Object.values(limitFilters);
    const anyTrue = values.some((value) => value === true);

    if (anyTrue) return true;
    return false; // If none are true, return false
  }, [limitFilters]);

  const { data: anomalousAnalyzeTags } = useAnomalousTags(
    payload,
    anomalousSystems || [],
    viewState.config?.[ViewType.ANALYZE]?.sortOption || AnomalySort.SEVERITY,
    showAnalyzeLimits,
  );

  // Common function to get tags based on model type
  const getModelTags = useCallback((): Tag[] => {
    const modelTags: Tag[] = [];

    if (model.type === (DeployedModelType.DECONSTRUCTED_MODEL as unknown as ModelType)) {
      // For DECONSTRUCTED_MODEL, use anomalous tags in order but only if they exist in independent tags
      const independentTags = model.independent_tags || [];
      const anomalousTagsInOrder =
        anomalousAnalyzeTags?.find((tag) => tag.folder === model.process_id)?.tags || [];

      // Filter anomalous tags to only include those that exist in independent tags
      const filteredAnomalousTags = anomalousTagsInOrder.filter((tagName) =>
        independentTags.includes(tagName),
      );

      filteredAnomalousTags.forEach((tagName) => {
        const tagObj = getTag(tagName, model.process_id)[0];
        if (tagObj) {
          modelTags.push(tagObj);
        }
      });
    } else {
      // For other model types, add OpMode tag, dependent tags, and independent tags

      // Add OpMode tag
      if (model.op_mode) {
        const opModeTag = getTag(model.op_mode)[0];
        if (opModeTag) {
          modelTags.push(opModeTag);
        }
      }

      // Add dependent tags
      const dependentTags = model.dependent_tags || [];
      dependentTags.forEach((tag) => {
        const tagObj = getTag(tag.name, model.process_id)[0];
        if (tagObj) {
          modelTags.push(tagObj);
        }
      });

      // Add independent tags
      const independentTags = model.independent_tags || [];
      independentTags.forEach((tagName) => {
        const tagObj = getTag(tagName)[0];
        if (tagObj) {
          modelTags.push(tagObj);
        }
      });
    }

    // Remove duplicates
    return modelTags.filter(
      (tag, index, self) => index === self.findIndex((t) => t.name === tag.name),
    );
  }, [model, anomalousAnalyzeTags, getTag]);

  // Check if current model tags are selected for investigation
  const isCurrentModelInvestigated = useMemo(() => {
    const modelTags = getModelTags();

    // Check if all model tags are already selected across all folders
    return modelTags.every((tag) => {
      const folder = tag.folder || -1;
      const currentSelectedTags = viewState.view[ViewType.INVESTIGATE].selectedTags[folder] || [];
      return currentSelectedTags.some((selectedTag) => selectedTag.name === tag.name);
    });
  }, [viewState.view, getModelTags]);

  const handleInvestigate = () => {
    if (isCurrentModelInvestigated) {
      // Clear investigation for this model across all folders
      const modelTags = getModelTags();
      const tagsByFolder: Record<number, Tag[]> = {};
      modelTags.forEach((tag) => {
        const folder = tag.folder || -1;
        if (!tagsByFolder[folder]) {
          tagsByFolder[folder] = [];
        }
        tagsByFolder[folder].push(tag);
      });

      setViewState((prev) => {
        const newSelectedTags = { ...prev.view[ViewType.INVESTIGATE].selectedTags };

        // Remove model tags from each folder
        Object.keys(tagsByFolder).forEach((folder) => {
          const folderKey = Number(folder);
          const modelTagNames = tagsByFolder[folderKey].map((tag) => tag.name);
          newSelectedTags[folderKey] = (newSelectedTags[folderKey] || []).filter(
            (selectedTag) => !modelTagNames.includes(selectedTag.name),
          );
        });

        return {
          ...prev,
          view: {
            ...prev.view,
            [ViewType.INVESTIGATE]: {
              ...prev.view[ViewType.INVESTIGATE],
              selectedTags: newSelectedTags,
            },
          },
        };
      });
    } else {
      // Add all model tags to investigation
      const uniqueTags = getModelTags();

      // Group tags by their folder
      const tagsByFolder: Record<number, Tag[]> = {};
      uniqueTags.forEach((tag) => {
        const folder = tag.folder || -1;
        if (!tagsByFolder[folder]) {
          tagsByFolder[folder] = [];
        }
        tagsByFolder[folder].push(tag);
      });

      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.INVESTIGATE]: {
            ...prev.view[ViewType.INVESTIGATE],
            selectedTags: {
              ...prev.view[ViewType.INVESTIGATE].selectedTags,
              ...tagsByFolder,
            },
          },
        },
      }));
    }
    onClose();
  };

  // Check if current model tags are pinned
  const isCurrentModelPinned = useMemo(() => {
    const modelTags = getModelTags();
    return modelTags.some((tag) =>
      viewState.view[ViewType.ANALYZE].pinnedTags?.some(
        (pinnedTag) => pinnedTag.name === tag.name && pinnedTag.folder === model.process_id,
      ),
    );
  }, [viewState.view, model.process_id, getModelTags]);

  const handlePinAllTags = () => {
    if (isCurrentModelPinned) {
      // Unpin all tags from this model
      const modelTags = getModelTags();
      const modelTagNames = modelTags.map((tag) => tag.name);

      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.ANALYZE]: {
            ...prev.view[ViewType.ANALYZE],
            pinnedTags: (prev.view[ViewType.ANALYZE].pinnedTags || []).filter((pinnedTag) => {
              // Keep tags that are not from this model
              return !(
                pinnedTag.folder === model.process_id && modelTagNames.includes(pinnedTag.name)
              );
            }),
          },
        },
      }));
    } else {
      // Pin all tags from this model
      const modelTags = getModelTags();

      // Convert to ParentTag format for pinning
      const tagsToPin: ParentTag[] = modelTags as ParentTag[];

      setViewState((prev) => ({
        ...prev,
        view: {
          ...prev.view,
          [ViewType.ANALYZE]: {
            ...prev.view[ViewType.ANALYZE],
            pinnedTags: [...(prev.view[ViewType.ANALYZE].pinnedTags || []), ...tagsToPin],
          },
        },
      }));
    }
    onClose();
  };

  const handleModelDetails = () => {
    navigate(
      `${generatePath(Paths.MANAGE_V2_MODELS, {
        tenantId: tenant.toString(),
      })}?folder=${model.process_id}&ensembleId=${model.ensemble_id}`,
    );
    onClose();
  };
  const handleRelatedModels = () => {
    navigate(
      `${generatePath(Paths.MANAGE_V2_MODELS, {
        tenantId: tenant.toString(),
      })}?folder=${model.process_id}`,
    );
    onClose();
  };

  return (
    <Dropdown.Options position="right" open={true}>
      <Dropdown.Title>Model Actions</Dropdown.Title>
      <Dropdown.Item onClick={handleInvestigate}>
        {isCurrentModelInvestigated ? 'Remove from Investigate' : 'Investigate all tags'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handlePinAllTags}>
        {isCurrentModelPinned ? 'Unpin all tags' : 'Pin all tags'}
      </Dropdown.Item>
      {model.type !== (DeployedModelType.DECONSTRUCTED_MODEL as unknown as ModelType) && (
        <>
          <Dropdown.Title>Manage</Dropdown.Title>
          <Dropdown.Item onClick={handleModelDetails}>Model</Dropdown.Item>
          <Dropdown.Item onClick={handleRelatedModels}>Related models</Dropdown.Item>
        </>
      )}
    </Dropdown.Options>
  );
};
